import { createFileRoute } from '@tanstack/react-router';
import { HomePage } from '../features/home';
import { emptyHomeData, homeQuery, type HomeData } from '../lib/queries';
import {
  articleUrl,
  DEFAULT_OG,
  ogImageMeta,
  ORGANIZATION_ID,
  SITE_LOCALE,
  SITE_NAME,
  SITE_URL,
  WEBSITE_ID,
} from '../lib/seo';

const TITLE = 'الرئيسية — تلي صافي | Telesafi.ma';
const DESC = 'أخبار المغرب والعالم: السياسة، الاقتصاد، الجهات، الرياضة والثقافة على تلي صافي.';

/**
 * The lead stories, as an ordered list. The home page is the entry point most
 * crawlers and answer engines hit first, and without this the only machine-
 * readable thing on it is the organization block — nothing says what is
 * currently being published.
 */
function homeJsonLd(home: HomeData): string {
  const seen = new Set<string>();
  const lead = [...home.last24Hours, ...home.highlights, ...home.nationals]
    .filter((a) => {
      if (!a.slug || seen.has(a.slug)) return false;
      seen.add(a.slug);
      return true;
    })
    .slice(0, 15);
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': SITE_URL,
    url: SITE_URL,
    name: SITE_NAME,
    description: DESC,
    inLanguage: 'ar',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORGANIZATION_ID },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: lead.length,
      itemListElement: lead.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: articleUrl(a.slug),
        name: a.title,
      })),
    },
  });
}

export const Route = createFileRoute('/')({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(homeQuery()).catch(() => emptyHomeData),
  head: ({ match }) => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESC },
      { property: 'og:title', content: TITLE },
      { property: 'og:description', content: DESC },
      { property: 'og:url', content: SITE_URL },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: SITE_LOCALE },
      ...ogImageMeta({ ...DEFAULT_OG, alt: SITE_NAME }),
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: TITLE },
      { name: 'twitter:description', content: DESC },
    ],
    links: [{ rel: 'canonical', href: SITE_URL }],
    scripts: (() => {
      const home = (match.loaderData as HomeData | undefined) ?? undefined;
      if (!home) return [];
      const jsonLd = homeJsonLd(home);
      return [{ type: 'application/ld+json', children: jsonLd }];
    })(),
  }),
  component: HomePage,
});
